import { useEffect, useRef, useState, useCallback } from 'react'
import {
  apiMatchLogList, apiMatchStats, apiMatchMomList,
  apiMatchLiveConsole, apiMatchLiveSub, apiMatchLiveFormation,
} from '../server/ServiceCall'
import { AuthState } from '../store/auth'

interface Props {
  auth:        AuthState
  fixtureCode: number
  myClubCode:  number
  onBack:      () => void
}

interface FixtureInfo {
  FIXTURE_CODE:   number
  STATUS:         number
  ROUND_NO:       number
  MATCH_DATE:     string
  HOME_CLUB:      number
  HOME_NAME:      string
  AWAY_CLUB:      number
  AWAY_NAME:      string
  HOME_GOAL:      number
  AWAY_GOAL:      number
  HOME_FORMATION: number | null
  AWAY_FORMATION: number | null
}

interface LogRow {
  LOG_CODE:    number
  MINUTE_NO:   number
  EVENT_TYPE:  number
  TEAM_SIDE:   number
  ACTOR_CODE:  number | null
  TARGET_CODE: number | null
  NARRATIVE:   string
  HOME_GOAL:   number
  AWAY_GOAL:   number
}

interface StatsRow {
  HOME_SHOT: number; AWAY_SHOT: number
  HOME_GOAL: number; AWAY_GOAL: number
  HOME_ON_TARGET: number; AWAY_ON_TARGET: number
  HOME_PASS: number; AWAY_PASS: number
  HOME_DRIBBLE: number; AWAY_DRIBBLE: number
  HOME_YELLOW: number; AWAY_YELLOW: number
  HOME_RED: number; AWAY_RED: number
  HOME_INJURY: number; AWAY_INJURY: number
  HOME_OFFSIDE: number; AWAY_OFFSIDE: number
  HOME_PENALTY: number; AWAY_PENALTY: number
  HOME_CORNER: number; AWAY_CORNER: number
  HOME_FREEKICK: number; AWAY_FREEKICK: number
}

// ── MoM·선수 평점 (Phase 8-C-5, 보상 영향 없음) ─────────────
interface MomPlayer {
  PLAYER_CODE: number; CLUB_CODE: number; TEAM_SIDE: number
  PLAYER_NAME: string; POSITION: string; RATING: number
  GOALS: number; ASSISTS: number; SHOTS: number; SAVES: number
  YELLOWS: number; REDS: number
}
interface RatingRow extends MomPlayer { IS_MOM: number }

// ── 라이브 교체 콘솔 스냅샷 (Phase 8-C-2) ───────────────────
interface LivePlayer {
  Code: number; Name: string; Position: string
  SlotPos: string; Ovr: number; Stamina: number; IsGk: boolean
}
interface LiveSnapshot {
  Minute: number; ClubCode: number; IsUser: boolean
  FormationCode: number; SubCount: number; SubMaxCount: number
  Starting: LivePlayer[]; Bench: LivePlayer[]
}

// 진형 8종 (라이브 변경용 — WFM_FORMATION_MASTER 1~8)
const FORMATIONS: { code: number, name: string }[] = [
  { code: 1, name: '4-4-2' },
  { code: 2, name: '4-3-3' },
  { code: 3, name: '4-2-3-1' },
  { code: 4, name: '3-5-2' },
  { code: 5, name: '3-4-3' },
  { code: 6, name: '5-3-2' },
  { code: 7, name: '4-1-4-1' },
  { code: 8, name: '4-5-1' },
]
const formationName = (code: number) => FORMATIONS.find(f => f.code === code)?.name ?? `진형${code}`

export default function Match({ auth, fixtureCode, myClubCode, onBack }: Props) {
  const [fixture, setFixture]   = useState<FixtureInfo | null>(null)
  const [logs,    setLogs]      = useState<LogRow[]>([])
  const [stats,   setStats]     = useState<StatsRow | null>(null)
  const [mom,     setMom]       = useState<MomPlayer | null>(null)
  const [ratings, setRatings]   = useState<RatingRow[]>([])
  const [err,     setErr]       = useState('')
  const [polling, setPolling]   = useState(true)
  const lastLogRef = useRef<number>(0)
  const scrollRef  = useRef<HTMLDivElement>(null)

  // ── 교체 콘솔 상태 ─────────────────────────────────────
  const [snap, setSnap]       = useState<LiveSnapshot | null>(null)
  const [outSel, setOutSel]   = useState<number | null>(null)
  const [inSel, setInSel]     = useState<number | null>(null)
  const [subMsg, setSubMsg]   = useState('')
  const [busy, setBusy]       = useState(false)

  const isLive = fixture?.STATUS === 1

  // 콘솔 스냅샷 폴링 (진행중 + 본인 구단 경기일 때만)
  const loadConsole = useCallback(async () => {
    try {
      const r = await apiMatchLiveConsole(auth.token, fixtureCode, myClubCode)
      if (r.resultCode !== '00') { setSnap(null); return }
      const obj = JSON.parse(r.resultData) as { snapshot: LiveSnapshot }
      setSnap(obj.snapshot ?? null)
    } catch { /* 콘솔은 보조 — 실패해도 경기 폴링 유지 */ }
  }, [auth.token, fixtureCode, myClubCode])

  useEffect(() => {
    let mounted = true
    let timer: ReturnType<typeof setTimeout> | null = null

    async function tick() {
      try {
        const r = await apiMatchLogList(auth.token, fixtureCode, lastLogRef.current)
        if (!mounted) return
        if (r.resultCode !== '00') { setErr(r.resultMessage || '폴링 실패'); return }
        const obj = JSON.parse(r.resultData) as { fixture: FixtureInfo, logs: LogRow[] }
        if (obj.fixture) setFixture(obj.fixture)
        if (obj.logs?.length) {
          setLogs(prev => [...prev, ...obj.logs])
          lastLogRef.current = obj.logs[obj.logs.length - 1].LOG_CODE
          setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 50)
        }
        // 진행중이면 교체 콘솔 스냅샷도 갱신
        if (obj.fixture && obj.fixture.STATUS === 1) {
          const isMine = obj.fixture.HOME_CLUB === myClubCode || obj.fixture.AWAY_CLUB === myClubCode
          if (isMine) loadConsole()
        }
        if (obj.fixture && obj.fixture.STATUS === 2) {
          setPolling(false)
          setSnap(null)
          apiMatchStats(auth.token, fixtureCode).then(sr => {
            if (sr.resultCode !== '00') return
            try { const so = JSON.parse(sr.resultData) as { stats: StatsRow }; setStats(so.stats) } catch {}
          }).catch(() => {})
          // MoM·선수 평점 (Phase 8-C-5, 보상 영향 없음 — 평점·기록만)
          apiMatchMomList(auth.token, fixtureCode).then(mr => {
            if (mr.resultCode !== '00') return
            try {
              const mo = JSON.parse(mr.resultData) as { mom: MomPlayer | null, ratings: RatingRow[] }
              setMom(mo.mom ?? null)
              setRatings(mo.ratings ?? [])
            } catch {}
          }).catch(() => {})
          return
        }
      } catch (e: any) {
        setErr(e?.message ?? '네트워크 오류')
      }
      if (mounted && polling) timer = setTimeout(tick, 2000)
    }
    tick()
    return () => { mounted = false; if (timer) clearTimeout(timer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token, fixtureCode, myClubCode])

  // ── 교체 실행 ──────────────────────────────────────────
  async function handleSub() {
    if (!snap || outSel == null || inSel == null) return
    setBusy(true); setSubMsg('교체 요청 중…')
    const r = await apiMatchLiveSub(auth.token, fixtureCode, myClubCode, outSel, inSel)
    setSubMsg(`${r.resultCode === '00' ? '✅' : '⚠️'} ${r.resultMessage}`)
    if (r.resultCode === '00') { setOutSel(null); setInSel(null); setTimeout(loadConsole, 300) }
    setBusy(false)
  }

  // ── 진형 변경 ──────────────────────────────────────────
  async function handleFormation(fc: number) {
    if (!snap || fc === snap.FormationCode) return
    setBusy(true); setSubMsg('진형 변경 요청 중…')
    const r = await apiMatchLiveFormation(auth.token, fixtureCode, myClubCode, fc)
    setSubMsg(`${r.resultCode === '00' ? '✅' : '⚠️'} ${r.resultMessage}`)
    if (r.resultCode === '00') setTimeout(loadConsole, 300)
    setBusy(false)
  }

  const subRemain = snap ? snap.SubMaxCount - snap.SubCount : 0
  const canSub = !!snap && snap.IsUser && subRemain > 0 && outSel != null && inSel != null && !busy
  const staCls = (s: number) => s < 40 ? 'sta low' : s < 60 ? 'sta mid' : 'sta'

  return (
    <div className="page-pad match-page">
      <div className="match-head">
        <button onClick={onBack}>← 일정으로</button>
        {fixture ? (
          <>
            <h2>R{fixture.ROUND_NO} · {(fixture.MATCH_DATE || '').slice(0, 10)}</h2>
            <div className="scoreboard">
              <span className="home">{fixture.HOME_NAME}</span>
              <span className="score">{fixture.HOME_GOAL} : {fixture.AWAY_GOAL}</span>
              <span className="away">{fixture.AWAY_NAME}</span>
            </div>
            <div className="status">
              {fixture.STATUS === 1 ? '진행중' : fixture.STATUS === 2 ? '경기 종료' : '예정'}
            </div>
          </>
        ) : <p>경기 정보 로딩 중…</p>}
      </div>

      {err && <p className="msg">{err}</p>}

      {/* ── 라이브 교체 콘솔 (진행중 + 본인 구단) ── */}
      {isLive && snap && snap.IsUser && (
        <div className="sub-console">
          <div className="sub-console-head">
            <h3>교체 콘솔 <span className="sub-min">{snap.Minute}'</span></h3>
            <div className="sub-formation">
              <label>진형</label>
              <select
                value={snap.FormationCode}
                disabled={busy}
                onChange={e => handleFormation(Number(e.target.value))}
              >
                {FORMATIONS.map(f => (
                  <option key={f.code} value={f.code}>{f.name}</option>
                ))}
              </select>
            </div>
            <div className={`sub-count${subRemain === 0 ? ' done' : ''}`}>
              교체 {snap.SubCount}/{snap.SubMaxCount}
              {subRemain === 0 && <span> · 소진</span>}
            </div>
          </div>

          <div className="sub-console-body">
            {/* OUT: 그라운드 선수 */}
            <div className="sub-col">
              <h4>OUT (그라운드)</h4>
              <ul className="sub-list">
                {snap.Starting.map(p => (
                  <li
                    key={p.Code}
                    className={`sub-item${outSel === p.Code ? ' sel' : ''}`}
                    onClick={() => setOutSel(outSel === p.Code ? null : p.Code)}
                  >
                    <span className="si-pos">{p.SlotPos}</span>
                    <span className="si-name">{p.Name}</span>
                    <span className="si-ovr">{p.Ovr}</span>
                    <span className={staCls(p.Stamina)}><span className="sta-num">{p.Stamina}</span></span>
                  </li>
                ))}
              </ul>
            </div>

            {/* IN: 후보 */}
            <div className="sub-col">
              <h4>IN (후보 {snap.Bench.length}명)</h4>
              <ul className="sub-list">
                {snap.Bench.length === 0 && <li className="muted">후보 없음</li>}
                {snap.Bench.map(p => (
                  <li
                    key={p.Code}
                    className={`sub-item${inSel === p.Code ? ' sel' : ''}`}
                    onClick={() => setInSel(inSel === p.Code ? null : p.Code)}
                  >
                    <span className="si-pos">{p.Position}</span>
                    <span className="si-name">{p.Name}</span>
                    <span className="si-ovr">{p.Ovr}</span>
                    <span className={staCls(p.Stamina)}><span className="sta-num">{p.Stamina}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="sub-console-foot">
            <button className="primary" onClick={handleSub} disabled={!canSub}>
              {subRemain === 0 ? '교체 한도 소진' : '교체 실행'}
            </button>
            {subMsg && <span className="sub-msg">{subMsg}</span>}
          </div>
        </div>
      )}

      {isLive && snap && !snap.IsUser && (
        <p className="muted sub-readonly">상대 구단 경기는 교체 콘솔을 사용할 수 없습니다.</p>
      )}

      <div className="match-log" ref={scrollRef}>
        {logs.length === 0 && <p className="muted">경기 시작을 기다리는 중…</p>}
        {logs.map(l => {
          const isGoal    = l.EVENT_TYPE === 4
          const isPeriod  = l.EVENT_TYPE === 8
          const isRed     = l.EVENT_TYPE === 6
          const isInjury  = l.EVENT_TYPE === 10
          const isSub     = l.EVENT_TYPE === 7
          const isOffside  = l.EVENT_TYPE === 11
          const isPenalty  = l.EVENT_TYPE === 12
          const isCorner   = l.EVENT_TYPE === 13
          const isFreekick = l.EVENT_TYPE === 14
          const isThrowin  = l.EVENT_TYPE === 15
          let cls = `log-row side-${l.TEAM_SIDE}`
          if (isGoal)          cls = 'log-row goal'
          else if (isPeriod)   cls = 'log-row period'
          else if (isRed)      cls = 'log-row red'
          else if (isInjury)   cls = 'log-row injury'
          else if (isSub)      cls = 'log-row sub'
          else if (isOffside)  cls = 'log-row offside'
          else if (isPenalty)  cls = 'log-row penalty'
          else if (isCorner)   cls = 'log-row corner'
          else if (isFreekick) cls = 'log-row freekick'
          else if (isThrowin)  cls = 'log-row throwin'
          return (
            <div key={l.LOG_CODE} className={cls}>
              <span className="min">{l.MINUTE_NO}'</span>
              <span className="text">{l.NARRATIVE}</span>
              {isGoal && <span className="cur">({l.HOME_GOAL}-{l.AWAY_GOAL})</span>}
            </div>
          )
        })}
      </div>

      {stats && fixture && (
        <div className="match-stats">
          <h3>경기 통계</h3>
          <table>
            <thead>
              <tr><th className="t-home">{fixture.HOME_NAME}</th><th>지표</th><th className="t-away">{fixture.AWAY_NAME}</th></tr>
            </thead>
            <tbody>
              <StatRow label="슛 시도"   home={stats.HOME_SHOT}      away={stats.AWAY_SHOT} />
              <StatRow label="유효슛"    home={stats.HOME_ON_TARGET} away={stats.AWAY_ON_TARGET} />
              <StatRow label="득점"      home={stats.HOME_GOAL}      away={stats.AWAY_GOAL} />
              <StatRow label="패스"      home={stats.HOME_PASS}      away={stats.AWAY_PASS} />
              <StatRow label="드리블"    home={stats.HOME_DRIBBLE}   away={stats.AWAY_DRIBBLE} />
              <StatRow label="옐로카드"  home={stats.HOME_YELLOW}    away={stats.AWAY_YELLOW} />
              <StatRow label="레드카드"  home={stats.HOME_RED}       away={stats.AWAY_RED} />
              <StatRow label="부상"      home={stats.HOME_INJURY}    away={stats.AWAY_INJURY} />
              <StatRow label="코너"      home={stats.HOME_CORNER}    away={stats.AWAY_CORNER} />
              <StatRow label="프리킥"    home={stats.HOME_FREEKICK}  away={stats.AWAY_FREEKICK} />
              <StatRow label="오프사이드" home={stats.HOME_OFFSIDE}   away={stats.AWAY_OFFSIDE} />
              <StatRow label="페널티"    home={stats.HOME_PENALTY}   away={stats.AWAY_PENALTY} />
            </tbody>
          </table>
          {(() => {
            const homeAct = stats.HOME_PASS + stats.HOME_DRIBBLE + stats.HOME_SHOT
            const awayAct = stats.AWAY_PASS + stats.AWAY_DRIBBLE + stats.AWAY_SHOT
            const total   = homeAct + awayAct
            if (total === 0) return null
            const homePct = Math.round(homeAct * 100 / total)
            const awayPct = 100 - homePct
            return (
              <div className="possession">
                <div className="poss-label">점유율 추정</div>
                <div className="poss-bar">
                  <div className="poss-home" style={{ width: `${homePct}%` }}>{homePct}%</div>
                  <div className="poss-away" style={{ width: `${awayPct}%` }}>{awayPct}%</div>
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {mom && fixture && (
        <div className="match-mom">
          <h3>Man of the Match</h3>
          <div className="mom-badge">
            <span className="mom-star">★</span>
            <span className="mom-name">{mom.PLAYER_NAME}</span>
            <span className="mom-pos">{mom.POSITION}</span>
            <span className="mom-team">{mom.TEAM_SIDE === 1 ? fixture.HOME_NAME : fixture.AWAY_NAME}</span>
            <span className="mom-rating">{mom.RATING.toFixed(1)}</span>
          </div>
          <div className="mom-line">
            {mom.GOALS > 0 && <span>골 {mom.GOALS}</span>}
            {mom.ASSISTS > 0 && <span>도움 {mom.ASSISTS}</span>}
            {mom.SHOTS > 0 && <span>슛 {mom.SHOTS}</span>}
            {mom.SAVES > 0 && <span>선방 {mom.SAVES}</span>}
          </div>
        </div>
      )}

      {ratings.length > 0 && fixture && (
        <div className="match-ratings">
          <h3>선수 평점</h3>
          <table>
            <thead>
              <tr><th>선수</th><th>팀</th><th>포지션</th><th>평점</th><th>기여</th></tr>
            </thead>
            <tbody>
              {ratings.map(r => (
                <tr key={r.PLAYER_CODE} className={r.IS_MOM ? 'is-mom' : ''}>
                  <td className="r-name">{r.IS_MOM === 1 && <span className="r-star">★</span>}{r.PLAYER_NAME}</td>
                  <td className="r-team">{r.TEAM_SIDE === 1 ? fixture.HOME_NAME : fixture.AWAY_NAME}</td>
                  <td className="r-pos">{r.POSITION}</td>
                  <td className="r-rating">{r.RATING.toFixed(1)}</td>
                  <td className="r-contrib">
                    {[
                      r.GOALS   > 0 ? `골${r.GOALS}`     : '',
                      r.ASSISTS > 0 ? `도움${r.ASSISTS}` : '',
                      r.SAVES   > 0 ? `선방${r.SAVES}`   : '',
                      r.YELLOWS > 0 ? `경고${r.YELLOWS}` : '',
                      r.REDS    > 0 ? `퇴장${r.REDS}`    : '',
                    ].filter(Boolean).join(' ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mom-note">※ 평점·MoM은 기록용입니다. 보상(자금·능력치·경험치·이적가치)에 영향을 주지 않습니다.</p>
        </div>
      )}
    </div>
  )
}

function StatRow({ label, home, away }: { label: string, home: number, away: number }) {
  const cls = home > away ? 'home-win' : home < away ? 'away-win' : ''
  return (
    <tr className={cls}>
      <td className="t-home">{home}</td>
      <td className="t-label">{label}</td>
      <td className="t-away">{away}</td>
    </tr>
  )
}
